import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Helmet } from 'react-helmet-async';
import 'leaflet/dist/leaflet.css';
import Sidebar from '../components/shared/Sidebar';
import ParameterModal from '../components/shared/ParameterModal';
import { MapTheme, MapView } from '../components/map/types';
import ViewToggle from '../components/map/ViewToggle';
import ThemeSelector from '../components/map/ThemeSelector';
import LiveSiteDetails from '../components/shared/LiveSiteDetails';
import { ApiLocation, fetchLocations } from '../services/api';
import { getMockLocations } from '../services/mockData';
import L from 'leaflet';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import LoadingIndicator from '../components/shared/LoadingIndicator';
import { RefreshCw } from 'lucide-react';

// Fix for marker icon issue in Leaflet with React
// This needs to be done once in the application
const fixLeafletIcon = () => {
  // @ts-ignore - Leaflet types are not complete
  delete L.Icon.Default.prototype._getIconUrl;
  
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  });
};

// Add this component for map control
function MapController({ center }: { center: [number, number] }) {
  const map = useMap();
  map.setView(center, 13);
  return null;
}

// Define map styles for different themes
const mapStyles = {
  streets: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
  },
  minimal: {
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  }
};

// Create a component to handle theme changes
function ThemeController({ theme }: { theme: MapTheme }) {
  return (
    <TileLayer
      url={mapStyles[theme].url}
      attribution={mapStyles[theme].attribution}
    />
  );
}

export default function Map() {
  // Fix Leaflet icon issue when component mounts
  useEffect(() => {
    fixLeafletIcon();
  }, []);

  const [locations, setLocations] = useState<ApiLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<ApiLocation | null>(null);
  const [selectedParameter, setSelectedParameter] = useState<null | any>(null);
  const [theme, setTheme] = useState<MapTheme>('streets');
  const [view, setView] = useState<MapView>('2D');
  const [manualRefreshKey, setManualRefreshKey] = useState<number>(0);
  const USE_MOCK_LOCATIONS = true; // Temporarily disable live API locations

  // Fetch locations when component mounts
  useEffect(() => {
    let isMounted = true;
    
    async function loadLocations() {
      try {
        setIsLoading(true);
        setError(null);
        
        const data = USE_MOCK_LOCATIONS ? getMockLocations() : await fetchLocations();
        
        if (isMounted && data.length > 0) {
          setLocations(data);
          setSelectedLocation(data[0]); // Select the first location by default
          setIsLoading(false);
        } else if (isMounted) {
          setError('No monitoring locations found.');
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching locations:', err);
        if (isMounted) {
          setError('Failed to load monitoring locations. Please try again later.');
          setIsLoading(false);
        }
      }
    }
    
    loadLocations();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleThemeChange = (newTheme: MapTheme) => {
    console.log('Changing map theme to:', newTheme);
    setTheme(newTheme);
  };

  const handleViewChange = (newView: MapView) => {
    console.log('View change clicked:', newView);
    setView(newView);
  };

  // Function to trigger data refresh
  const handleDataRefresh = () => {
    setManualRefreshKey(prev => prev + 1);
  };

  // Convert API location to map coordinates [lat, lng]
  const getCoordinates = (location: ApiLocation): [number, number] => {
    return [location.gCoord.x, location.gCoord.y];
  };

  // Error fallback component
  const ErrorFallback = (
    <div className="h-[calc(100vh-8rem)] w-full flex items-center justify-center bg-[#E6F7F4]/30 backdrop-blur-sm">
      <div className="text-center p-6 max-w-md bg-white/80 rounded-lg shadow-md">
        <h3 className="text-xl font-bold text-red-600 mb-2">Map Error</h3>
        <p className="text-gray-700 mb-4">
          We encountered an error loading the map. Please try again later.
        </p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#005E6E] text-white rounded-lg hover:bg-[#004957] transition-colors"
        >
          Reload
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="h-[calc(100vh-8rem)] w-full flex items-center justify-center bg-[#E6F7F4]/30 backdrop-blur-sm">
        <div className="flex flex-col items-center bg-white/80 p-8 rounded-lg shadow-md">
          <LoadingIndicator />
          <p className="text-[#005E6E] text-lg mt-4">Loading map data...</p>
        </div>
      </div>
    );
  }

  if (error || !selectedLocation) {
    return (
      <div className="h-[calc(100vh-8rem)] w-full flex items-center justify-center bg-[#E6F7F4]/30 backdrop-blur-sm">
        <div className="text-center p-6 max-w-md bg-white/80 rounded-lg shadow-md">
          <p className="text-red-500 mb-4">{error || 'An error occurred loading the map'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#005E6E] text-white rounded-lg hover:bg-[#004957] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <div className="h-[calc(100vh-8rem)] w-full relative bg-[#E6F7F4]/30 backdrop-blur-sm">
        <Helmet>
          <title>Interactive Map | In Deep Water</title>
          <meta name="description" content="Explore In Deep Water's interactive map showing coastal protection sites and environmental data across different locations." />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        </Helmet>

        {/* Location Chips and Refresh Button Container */}
        <div className="absolute top-4 left-4 z-[1000] flex flex-col gap-2">
          {/* Location Chips */}
          <div className="flex gap-2 flex-wrap">
            {locations.map((location) => (
              <button
                key={location.ID}
                onClick={() => setSelectedLocation(location)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all shadow-sm hover:shadow-md
                  ${selectedLocation.ID === location.ID 
                    ? 'bg-[#005E6E] text-white' 
                    : 'bg-white/70 backdrop-blur-sm border border-[#005E6E]/50 text-[#005E6E] hover:bg-white/90'}`}
              >
                {location.siteName}
              </button>
            ))}
          </div>
          {/* Refresh Data Button - underneath chips */}
          {selectedLocation && (
            <button 
              onClick={handleDataRefresh}
              className="mt-1 flex items-center justify-center px-4 py-2 bg-white text-[#005E6E] border-2 border-[#005E6E] rounded-full text-sm font-medium hover:bg-gray-100 shadow-sm transition-colors"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh Data
            </button>
          )}
        </div>

        <MapContainer
          center={getCoordinates(selectedLocation)}
          zoom={13}
          className="h-full w-full rounded-lg overflow-hidden bg-[#F8FAFB]/70 outline-none"
          zoomControl={false}
        >
          <MapController center={getCoordinates(selectedLocation)} />
          <ThemeController theme={theme} />
          
          {locations.map((location) => (
            <Marker 
              key={location.ID}
              position={getCoordinates(location)}
              eventHandlers={{
                click: () => setSelectedLocation(location)
              }}
            >
              <Popup>{location.siteName}</Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Map Controls - Bottom Left */}
        <div className="absolute left-4 bottom-4 z-[1000] flex flex-col gap-2 bg-white/60 backdrop-blur-sm p-3 rounded-lg shadow-md">
          <ViewToggle value={view} onChange={handleViewChange} />
          <ThemeSelector value={theme} onChange={handleThemeChange} />
        </div>

        <ErrorBoundary>
          <Sidebar 
            data={selectedLocation}
            mapTheme={theme}
            onMapThemeChange={handleThemeChange}
            mapView={view}
            onMapViewChange={handleViewChange}
          >
            <LiveSiteDetails 
              location={selectedLocation}
              refreshTrigger={manualRefreshKey}
              onParameterClick={setSelectedParameter}
            />
          </Sidebar>
        </ErrorBoundary>

        <ParameterModal
          isOpen={!!selectedParameter}
          onClose={() => setSelectedParameter(null)}
          parameter={selectedParameter}
        />
      </div>
    </ErrorBoundary>
  );
}